import { Card, CardContent } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  useGrouping,
  type GroupingOption,
  type GroupingConfig,
} from '@/components/queries/grouping-utils'

// Generic grouping component props
export interface GroupingProps<T> {
  /** Array of items to group and display */
  items: T[]
  /** Current grouping option */
  groupBy: GroupingOption
  /** Grouping configuration */
  config: GroupingConfig<T>
  /** Function to render the list of items in each group */
  renderList: (items: T[]) => React.ReactNode
  /** Custom className for the container */
  className?: string
  /** Whether to show group headers */
  showGroupHeaders?: boolean
}

// Generic grouping component
export function Grouping<T>({
  items,
  groupBy,
  config,
  renderList,
  className = '',
  showGroupHeaders = true,
}: GroupingProps<T>) {
  const groupedItems = useGrouping(items, groupBy, config)

  return (
    <div className={`space-y-6 ${className}`}>
      {Object.entries(groupedItems).map(([groupName, groupItems]) => (
        <Card key={groupName}>
          {showGroupHeaders && groupBy !== 'none' && (
            <div className="border-b bg-muted/50 px-6 py-3">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold">{groupName}</h2>
                <span className="text-sm text-muted-foreground">
                  {groupItems.length}{' '}
                  {groupItems.length === 1
                    ? config.itemLabels?.singular || 'item'
                    : config.itemLabels?.plural || 'items'}
                </span>
              </div>
            </div>
          )}
          <CardContent className="p-4">{renderList(groupItems)}</CardContent>
        </Card>
      ))}
    </div>
  )
}

// Generic grouping selector props
export interface GroupingSelectorProps<TOption extends string> {
  /** Current grouping option */
  value: TOption
  /** Callback when grouping changes */
  onChange: (value: TOption) => void
  /** Available grouping options */
  options: readonly TOption[]
  /** Labels for each option */
  optionLabels: Record<TOption, string>
  /** Custom className for the container */
  className?: string
  /** Placeholder text */
  placeholder?: string
}

// Generic grouping selector component
export function GroupingSelector<TOption extends string>({
  value,
  onChange,
  options,
  optionLabels,
  className = '',
  placeholder = 'Group by...',
}: GroupingSelectorProps<TOption>) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className={`w-full sm:w-[180px] ${className}`}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem key={option} value={option}>
            {optionLabels[option]}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
